// Utility function to format date
export function formatEventDate(dateString) {
  const date = new Date(dateString);

  const options = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  };

  return date.toLocaleDateString('en-US', options);
}

export function flattenImage(image) {
  const node = image?.node || image?.nodes?.[0];

  if (!node) return null;

  return {
    id: node.id || '',
    alt: node.altText || '',
    caption: stripHTML(node.caption) || '',
    sizes: Array.isArray(node.mediaDetails?.sizes)
      ? node.mediaDetails.sizes.map((size) => ({
          width: size.width,
          height: size.height,
          url: size.sourceUrl,
        }))
      : [],
  };
}

export function flattenCustomImage(image) {
  const node = image?.node;

  if (!node) return null;

  return {
    id: node.id || '',
    alt: node.altText || '',
    caption: stripHTML(node.caption) || '',
    sourceUrl: node.sourceUrl || false,
    width: node.mediaDetails.width || false,
    height: node.mediaDetails.height || false,
    blurPlaceholder: node.blurPlaceholder || false,
  };
}

export function flattenVideo(video) {
  if (!video?.node) return null;

  return {
    id: video.node.id || '',
    caption: stripHTML(video.node.caption) || '',
    url: video.node.mediaItemUrl || null,
    width: video.node.mediaDetails?.width || null,
    height: video.node.mediaDetails?.height || null,
  };
}

// WordPress hands back taxonomy term names, titles and ACF text already
// HTML-encoded ("Marketing &amp; Community"). Anything we pipe through
// dangerouslySetInnerHTML gets decoded by the browser for free, but plain-text
// JSX (staff role, filter chips) renders the raw entity — so decode here in the
// transform layer rather than at each render site.
const NAMED_ENTITIES = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: '\u00a0',
  ndash: '–',
  mdash: '—',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  hellip: '…',
};

export function decodeEntities(value) {
  if (typeof value !== 'string' || !value.includes('&')) return value;

  return value.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (match, entity) => {
    if (entity[0] === '#') {
      const codePoint =
        entity[1].toLowerCase() === 'x'
          ? parseInt(entity.slice(2), 16)
          : parseInt(entity.slice(1), 10);
      // Bail on anything that isn't a valid scalar value rather than throwing.
      if (!Number.isFinite(codePoint) || codePoint > 0x10ffff) return match;
      return String.fromCodePoint(codePoint);
    }
    const decoded = NAMED_ENTITIES[entity.toLowerCase()];
    return decoded === undefined ? match : decoded;
  });
}

export function stripHTML(htmlString) {
  if (htmlString === null || typeof htmlString !== 'string') {
    return null;
  }
  return htmlString.replace(/<[^>]*>/g, '');
}

export function extractIframeData(oembed) {
  if (!oembed) return { src: null, title: null };
  const srcMatch = oembed.match(/src=["']([^"']+)["']/);
  const titleMatch = oembed.match(/title=["']([^"']+)["']/);
  return {
    src: srcMatch ? srcMatch[1] : null,
    title: titleMatch ? titleMatch[1] : null,
  };
}

// Media Library files (PDFs, images, etc.) are served by WordPress, not by
// this frontend — stripping their origin turns them into a 404 on our domain.
const WP_UPLOAD_PATH = /^\/wp-content\//;

function toCmsUrl(path) {
  const wpUrl = process.env.NEXT_PUBLIC_WORDPRESS_URL?.replace(/\/+$/, '');
  return wpUrl ? `${wpUrl}${path}` : path;
}

export function toRelativeUrl(url) {
  if (!url) return url;
  if (WP_UPLOAD_PATH.test(url)) return toCmsUrl(url);
  const urlWithoutProtocol = url.replace(/^https?:\/\//, '');
  const internalOrigins = [
    process.env.NEXT_SITE_URL,
    process.env.NEXT_PUBLIC_WORDPRESS_URL,
  ].filter(Boolean);

  for (const origin of internalOrigins) {
    const siteOrigin = origin.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    if (urlWithoutProtocol.startsWith(siteOrigin)) {
      const path = urlWithoutProtocol.slice(siteOrigin.length);
      if (WP_UPLOAD_PATH.test(path)) return toCmsUrl(path);
      return path.replace(/\/+$/, '') || '/';
    }
  }
  return url;
}

export function transformCta(cta) {
  if (!cta || cta.type === 'none') return false;

  const isDocument = cta.type === 'document';
  const isPage = cta.type === 'page';

  let url = false;
  let buttonLabel = false;
  let isExternal = false;

  if (isDocument) {
    url = cta.file?.node?.mediaItemUrl || false;
    buttonLabel = cta.buttonLabel || false;
    isExternal = true;
  } else if (isPage) {
    const linkedPage = cta.page?.nodes?.[0] || false;
    url = toRelativeUrl(linkedPage?.uri || false);
    buttonLabel = cta.buttonLabel || linkedPage?.title || false;
    isExternal = false;
  } else {
    url = toRelativeUrl(cta.link?.url || false);
    buttonLabel = cta.link?.title || false;
    isExternal =
      cta.link?.target === '_blank' || url?.startsWith('http') || false;
  }

  return {
    buttonLabel,
    type: cta.type,
    url,
    isExternal,
  };
}

export function flattenMenuItems(items = []) {
  return items.map((item) => ({
    id: item.id,
    label: item.label,
    uri: item.uri,
    target: item.target,
    children: item.childItems?.nodes?.length
      ? flattenMenuItems(item.childItems.nodes)
      : [],
  }));
}

export function buildMenuTree(flatItems) {
  const itemsById = {};
  const rootItems = [];

  flatItems.forEach((item) => {
    itemsById[item.id] = {
      ...item,
      uri: item.uri?.replace(/\/$/, '') || '/', // Normalize URI here
      children: [],
    };
  });

  flatItems.forEach((item) => {
    const parentId = item.parentId;

    if (parentId && itemsById[parentId]) {
      itemsById[parentId].children.push(itemsById[item.id]);
    } else {
      rootItems.push(itemsById[item.id]);
    }
  });

  return rootItems;
}

export function transformCard(card = {}) {
  // console.log(card);

  // People cards show their role under the name. Neither the staff nor the
  // board-members field group registers a `subheading` field any more (see
  // inc/customPostTypes/staff.php — it's commented out), so the role taxonomy
  // is the source of truth. Staff imported before the field was removed still
  // carry stale `subheading` meta that `get_field('subheading')` happily
  // returns, so the role term must win over it — otherwise those people keep
  // rendering whatever their import set, not the role they're filed under.
  const ROLE_TAXONOMIES = ['staff-role', 'board-members-role'];
  const role = Array.isArray(card.taxonomies)
    ? card.taxonomies.find((term) => ROLE_TAXONOMIES.includes(term.taxonomy))
        ?.name
    : null;

  return {
    heading: card.heading || false,
    subheading: decodeEntities(role || card.subheading || false),
    description: card.description || false,
    label: decodeEntities(card.label || false),
    // Events only. Free-text strings from the events CPT (`date` / `time`), so
    // they're rendered as-is rather than parsed — see CardFormatter::formatPost.
    eventDate: card.eventDate || false,
    eventTime: card.eventTime || false,
    databaseId: card.databaseId || null,
    hasDeepDive: !!card.hasDeepDive,
    link: card.link
      ? {
          target: card.link.target || false,
          title: card.link.title || false,
          url: toRelativeUrl(card.link.url || false),
        }
      : false,
    // `CardPostImage` (theme-side) returns `altText`, but CustomImage reads
    // `alt`. Passing the raw object through left `alt` undefined, which tripped
    // CustomImage's `decorative` branch and put aria-hidden="true" on every
    // card image on the site — so the mapping has to be explicit.
    image: card.image?.sourceUrl
      ? {
          sourceUrl: card.image.sourceUrl,
          alt: card.image.altText || '',
          width: card.image.width || false,
          height: card.image.height || false,
          blurPlaceholder: card.image.blurPlaceholder || false,
        }
      : false,
    // videoUrl: card.videoUrl || false,
    // videoUrl:
    //   'https://vz-a28667fb-df9.b-cdn.net/408a70fc-9e98-45ed-858d-9599f50d3d7f/playlist.m3u8',
    taxonomies: Array.isArray(card.taxonomies)
      ? card.taxonomies.map((term) => ({
          name: decodeEntities(term.name),
          slug: term.slug,
          taxonomy: term.taxonomy,
          theme: term.slug,
        }))
      : [],
  };
}
